import { useNavigate } from 'react-router-dom';

import {
  LifeReadinessCategory,
  LifeReadinessQuestion,
  LifeReadinessSubcategory,
  LifeReadinessQuestionUserLevel,
} from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';

import { Box, SxProps, Theme, useMediaQuery } from '@mui/material';

import { CategoryCard } from './molecules/CategoryCard';
import { DashboardHeader } from './molecules/DashboardHeader';
import { ScoreExplanationTable } from './molecules/ScoreExplanationTable';
import { getProgressCountsForCategory } from './utils/transformScoresToProgressBar';

const FIGMA_WIDTH = 936;

type LifeReadinessDashboardProps = Readonly<{
  categories: LifeReadinessCategory[];
  subcategories: LifeReadinessSubcategory[];
  questions: LifeReadinessQuestion[];
  userLevels: LifeReadinessQuestionUserLevel[];
}>;

export const LifeReadinessDashboard = ({
  categories,
  subcategories,
  questions,
  userLevels,
}: LifeReadinessDashboardProps) => {
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery('(max-width: 900px)');

  const firstSubcategoryNameForCategory = (
    category: LifeReadinessCategory,
  ): LifeReadinessCategory['name'] => {
    const subcategoriesForCategory = subcategories
      .filter((subcategory) => subcategory.life_survey_category_id === category.id)
      .sort((a, b) => a.sequence - b.sequence);

    return subcategoriesForCategory[0].name.toLowerCase();
  };

  return (
    <Box sx={containerSx}>
      <DashboardHeader />

      {/* KNOWLEDGE CARD FIRST ON SMALL SCREENS, SECOND ON LARGE */}
      <Box sx={firstRowContainerSx}>
        <Box sx={knowledgeCardSx}>
          <ScoreExplanationTable />
        </Box>

        <Box sx={firstImageCardSx}>
          <CategoryCard
            key={categories[0].id}
            imageSrc={`/images/stickers/${categories[0].slug}.png`}
            title={categories[0].name}
            description={categories[0].description}
            progressBars={getProgressCountsForCategory(
              categories[0],
              subcategories,
              questions,
              userLevels,
            )}
            onClick={() => {
              navigate(
                `${APP_ROUTE.LIFE_READINESS}/${categories[0].slug}/${firstSubcategoryNameForCategory(
                  categories[0],
                )}`,
              );
            }}
          />
        </Box>
      </Box>

      {/* REMAINING CATEGORY CARDS */}
      <Box sx={imageCardsGroupContainerSx}>
        {categories.slice(isSmallScreen ? 0 : 1).map((category) => (
          <CategoryCard
            key={category.id}
            imageSrc={`/images/stickers/${category.slug}.png`}
            title={category.name}
            description={category.description}
            progressBars={getProgressCountsForCategory(
              category,
              subcategories,
              questions,
              userLevels,
            )}
            onClick={() => {
              navigate(
                `${APP_ROUTE.LIFE_READINESS}/${category.slug}/${firstSubcategoryNameForCategory(
                  category,
                )}`,
              );
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  margin: { sm: '0 auto' },
  p: {
    xs: backrs.spacing['spacing-2xl'],
    md: backrs.spacing['spacing-4xl'],
  },
});

const imageCardsGroupContainerSx: SxProps<Theme> = {
  display: {
    xs: 'flex',
    sm: 'grid',
  },
  flexDirection: 'column',

  gap: '2rem',

  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, 1fr)',
    lg: 'repeat(3, 1fr)',
  },
  '@media (max-width: 712px)': {
    gridTemplateColumns: 'repeat(1, 1fr)',
  },
  '@media (max-width: 1180px) and (max-height: 820px)': {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
  justifyContent: {
    xs: 'flex-start',
    md: 'center',
    lg: 'center',
  },
  justifyItems: {
    xs: 'flex-start',
    md: 'left',
    lg: 'center',
  },

  width: { xs: 'fit-content', lg: `${FIGMA_WIDTH}px` },
  mx: {
    xs: 'auto',
    md: 'unset',
    lg: 'auto',
  },
};

const firstRowContainerSx: SxProps<Theme> = ({ backrs }) => ({
  ...imageCardsGroupContainerSx,

  gridTemplateAreas: {
    xs: `
    "knowledge"
    "firstCard"
    `,
    md: `
    "firstCard knowledge"
    `,
  },

  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(1, 1fr)',
    lg: 'repeat(3, 1fr)',
  },
  '@media (max-width: 1180px)': {
    gridTemplateColumns: 'repeat(1, 1fr)',
  },

  display: {
    xs: 'flex',
    md: 'grid',
  },

  mb: backrs.spacing['spacing-4xl'],
});

const knowledgeCardSx: SxProps<Theme> = {
  gridArea: 'knowledge',
  gridColumn: {
    xs: 'auto',
    lg: 'span 2',
  },
};

const firstImageCardSx: SxProps<Theme> = {
  gridArea: 'firstCard',
  display: {
    xs: 'none',
    md: 'flex',
  },
};
